import { useState } from "react";
import { TFunction } from "i18next";
import Papa from "papaparse";

import useFileEncodingConversion from "@/hooks/useFileEncodingConversion";

export type EnrichData = Record<string, string>[];

export type UseCsvUploaderOptions = {
  rowLimit: number;
  fileLimit?: number;
  t: TFunction;
};

export type UseCsvUploaderReturn = {
  csvData: EnrichData | null;
  filename: string;
  columnNames: string[];
  guessedColumnsMapping: Record<string, string>;
  error: CsvUploaderError | null;
  isProcessing: boolean;
  processFile: (file: File) => void;
  reset: () => void;
  resetError: () => void;
  mapColumns: (mappings: Record<string, string>[], isPreMapped?: boolean) => EnrichData | undefined;
  mappedData: EnrichData;
  validRowCount: number;
};

export enum CsvUploaderError {
  FILE_LIMIT_EXCEEDED = "FILE_LIMIT_EXCEEDED",
  EMPTY_FILE = "EMPTY_FILE",
  ROW_LIMIT_EXCEEDED = "ROW_LIMIT_EXCEEDED",
  PARSING_ERROR = "PARSING_ERROR",
}

export enum TargetColumn {
  COMPANY_NAME = "company_name",
  DOMAIN = "domain",
  EMAIL = "email",
}

const defaultFileLimitMB = 10;

export const useCsvUploader = ({
  rowLimit,
  fileLimit = defaultFileLimitMB * 1024 * 1024,
  t,
}: UseCsvUploaderOptions): UseCsvUploaderReturn => {
  const [csvData, setCsvData] = useState<EnrichData | null>(null);
  const [columnNames, setColumnNames] = useState<string[]>([]);
  const [filename, setFilename] = useState<string>("");
  const [error, setError] = useState<CsvUploaderError | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [guessedColumnsMapping, setGuessedColumnsMapping] = useState<Record<string, string>>({});
  const [mappedColumns, setMappedColumns] = useState<Record<string, string | undefined>>({});
  const [mappedData, setMappedData] = useState<EnrichData>([]);
  const [validRowCount, setValidRowCount] = useState(0);

  const { convertFileToUTF8 } = useFileEncodingConversion();

  // Regex patterns for validation
  const domainRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const processFile = async (file: File) => {
    if (!file) {
      return;
    }

    if (file.size > fileLimit) {
      setError(CsvUploaderError.FILE_LIMIT_EXCEEDED);
      return;
    }

    setFilename(file.name.split(".csv")[0]);
    setIsProcessing(true);
    setError(null);
    setCsvData(null);
    setMappedColumns({});
    setGuessedColumnsMapping({});

    const convertedFile = await convertFileToUTF8(file);
    Papa.parse(convertedFile.file, {
      header: true,
      skipEmptyLines: "greedy",
      encoding: "utf-8",
      worker: true,
      complete: (result) => {
        let data = result.data as EnrichData;

        if (data.length === 0) {
          setError(CsvUploaderError.EMPTY_FILE);
          setIsProcessing(false);
          return;
        }

        const allKeys = Object.keys(data[0]);

        // find keys where every row is empty/null/undefined
        const keysToDrop = allKeys.filter((key) => data.every((r) => r[key] === "" || r[key] == null));

        data = data.map((item) => {
          const newItem: Record<string, string> = {};
          let unnamedCount = 1;

          Object.entries(item).forEach(([key, value]) => {
            if (keysToDrop.includes(key)) {
              // skip columns that were completely empty
              return;
            }

            // replace dots in the key
            const sanitizedKey = key.replaceAll(".", "_");

            // if the key is now blank or matches an auto-generated pattern,
            // generate an "Unnamed Column {count}"
            if (sanitizedKey === "" || /^_(\d+)$/.test(sanitizedKey)) {
              const unnamedCol = t("enrich.map.unnamedCol", { index: unnamedCount });
              newItem[unnamedCol] = value ?? "";
              unnamedCount++;
            } else {
              newItem[sanitizedKey] = value ?? "";
            }
          });

          return newItem;
        });

        const columnNames = Object.keys(data[0]);
        setColumnNames(columnNames);

        const guessedColumns = guessColumnMapping(data[0], columnNames);
        setGuessedColumnsMapping(guessedColumns);

        const limitedData = data.slice(0, rowLimit);

        setCsvData(limitedData);
        setIsProcessing(false);

        if (data.length > rowLimit) {
          setError(CsvUploaderError.ROW_LIMIT_EXCEEDED);
        }
      },
      error: () => {
        setError(CsvUploaderError.PARSING_ERROR);
        setIsProcessing(false);
      },
    });
  };

  const guessColumnMapping = (firstRow: EnrichData[0], columnNames: string[]): Record<string, string> => {
    const mappings: Record<string, string> = {};
    const usedTargets = new Set<string>();

    columnNames.forEach((column) => {
      const value = firstRow[column];

      // Check for Email
      if (emailRegex.test(value) && !usedTargets.has(TargetColumn.EMAIL)) {
        mappings[column] = TargetColumn.EMAIL;
        usedTargets.add(TargetColumn.EMAIL);
        return;
      }

      // Check for Domain
      if (domainRegex.test(value) && !usedTargets.has(TargetColumn.DOMAIN)) {
        mappings[column] = TargetColumn.DOMAIN;
        usedTargets.add(TargetColumn.DOMAIN);
        return;
      }

      // Assign to Company Name if not already assigned
      if (value && value.trim() !== "" && !usedTargets.has(TargetColumn.COMPANY_NAME)) {
        mappings[column] = TargetColumn.COMPANY_NAME;
        usedTargets.add(TargetColumn.COMPANY_NAME);
        return;
      }

      // If none match, leave it unmapped or handle accordingly
      mappings[column] = "";
    });

    return mappings;
  };

  const validateCell = (key: string, value: string): boolean => {
    switch (key) {
      case TargetColumn.COMPANY_NAME:
        return value.trim() !== "";
      case TargetColumn.EMAIL:
        return emailRegex.test(value);
      case TargetColumn.DOMAIN:
        return domainRegex.test(value);
      default:
        return false;
    }
  };

  const mapColumns = (mappings: Record<string, string>[], isPreMapped?: boolean) => {
    if (!csvData) return;

    // Prepare a set of already mapped columns for quick lookup
    const alreadyMappedKeys = new Set(Object.values(mappedColumns).filter((value): value is string => !!value));

    // Filter valid mappings (skip already mapped keys/values)
    const validMappings = mappings.flatMap((mapping) =>
      Object.entries(mapping).filter(
        ([originalColumn, newKey]) => mappedColumns[originalColumn] === undefined && !alreadyMappedKeys.has(newKey),
      ),
    );

    // Update mappedColumns and CSV data
    let tmpMappedColumns = { ...mappedColumns };
    setMappedColumns((prev) => {
      const updatedMapped = { ...prev };
      validMappings.forEach(([originalColumn, newKey]) => {
        updatedMapped[originalColumn] = newKey;
      });
      tmpMappedColumns = updatedMapped;
      return updatedMapped;
    });

    // Update CSV data by remapping the valid columns
    const updatedData = csvData.map((row) => {
      const newRow = { ...row }; // Start with a copy of the existing row
      validMappings.forEach(([originalColumn, newKey]) => {
        // Map the original column value to the new key
        newRow[newKey] = row[originalColumn] || ""; // Assign value from the original column
        delete newRow[originalColumn]; // Remove the old key
      });
      return newRow;
    });
    setCsvData(updatedData);

    const tmpMapping = isPreMapped ? mappings.reduce((acc, mapping) => ({ ...acc, ...mapping }), {}) : tmpMappedColumns;
    // Create a Set for faster lookup
    const mappedKeysSet = new Set(Object.values(tmpMapping).filter((key): string | undefined => key));

    // Extract only mapped data, ensuring keys exist and values are non-empty
    const extractedMappedData = updatedData.map((row) => {
      const filteredRow: Record<string, string> = {};
      Object.keys(row).forEach((key) => {
        if (mappedKeysSet.has(key) && row[key].trim() !== "") {
          filteredRow[key] = row[key];
        }
      });
      return filteredRow;
    });
    setMappedData(extractedMappedData);

    // Perform validation and count valid rows
    let count = 0;
    extractedMappedData.forEach((row) => {
      const keys = Object.keys(row);
      const isRowValid = keys.some((key) => validateCell(key, row[key]));
      if (isRowValid) {
        count++;
      }
    });
    setValidRowCount(count);

    return extractedMappedData;
  };

  const reset = () => {
    setCsvData(null);
    setError(null);
    setValidRowCount(0);
    setMappedColumns({});
    setIsProcessing(false);
  };

  const resetError = () => {
    setError(null);
  };

  return {
    csvData,
    filename,
    columnNames,
    guessedColumnsMapping,
    error,
    isProcessing,
    processFile,
    validRowCount,
    mappedData,
    mapColumns,
    reset,
    resetError,
  };
};
